import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI, usersAPI, followupsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, Button, Input, Select, Textarea, PageHeader, Spinner, Modal } from '../../components/ui';
import {
  CUSTOMER_TYPES, LEAD_STAGES, PRIORITIES, LEAD_SOURCES, INDIA_STATES, FOLLOWUP_TYPES
} from '../../utils/constants';
import TypeSpecificFields from '../../components/TypeSpecificFields';
import toast from 'react-hot-toast';

const FIELD = ({ label, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
    {children}
    {error && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{error}</p>}
  </div>
);

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

export default function LeadNewPage() {
  const navigate = useNavigate();
  const { isAdmin, canViewAllLeads, canAssignLeads, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  const [form, setForm] = useState({
    customer_type: '',
    stage: 'New',
    priority: 'Medium',
    source: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    region: '',
    notes: '',
    assigned_to: '',
    // type_specific_data fields set dynamically
  });
  const [typeData, setTypeData] = useState({});
  const [errors, setErrors] = useState({});
  // Post-creation follow-up prompt
  const [createdLead, setCreatedLead] = useState(null);
  const [fuModal, setFuModal] = useState(false);
  const [fuForm, setFuForm] = useState({ type: 'Call', scheduled_at: '', notes: '' });
  const [fuSaving, setFuSaving] = useState(false);

  useEffect(() => {
    if (canAssignLeads) {
      usersAPI.getAll().then(r => setTeamMembers(r.data?.data?.users || [])).catch(() => {});
    }
  }, [canAssignLeads]);

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setT = (key, val) => setTypeData(d => ({ ...d, [key]: val }));

  // Phone duplicate check
  const checkDuplicate = async (phone, email) => {
    if (!phone && !email) return;
    try {
      const res = await leadsAPI.checkDuplicate({ phone, email });
      if (res.data?.data?.exists) setDuplicateWarning(res.data.data.lead_ids?.join(', '));
      else setDuplicateWarning(null);
    } catch {}
  };

  const validate = () => {
    const e = {};
    if (!form.customer_type) e.customer_type = 'Required';
    if (!form.contact_phone) e.contact_phone = 'Required';
    if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) e.contact_email = 'Invalid email';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const payload = { ...form, type_specific_data: typeData };
      if (!payload.assigned_to) delete payload.assigned_to;
      const res = await leadsAPI.create(payload);
      const lead = res.data?.data;
      toast.success(`Lead ${lead?.lead_id} created!`);
      // Prompt to schedule first follow-up before navigating
      setCreatedLead(lead);
      setFuModal(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  const skipFollowUp = () => {
    setFuModal(false);
    navigate(`/leads/${createdLead?._id}`);
  };

  const saveFollowUp = async () => {
    if (!fuForm.scheduled_at) { toast.error('Date & time required'); return; }
    const year = new Date(fuForm.scheduled_at).getFullYear();
    if (year < 2000 || year > 2099) { toast.error('Please enter a valid year (2000–2099)'); return; }
    setFuSaving(true);
    try {
      await followupsAPI.create({ ...fuForm, lead_id: createdLead._id });
      toast.success('Follow-up scheduled!');
    } catch {
      toast.error('Lead created but follow-up failed — you can add it from the lead page');
    } finally {
      setFuSaving(false);
      setFuModal(false);
      navigate(`/leads/${createdLead?._id}`);
    }
  };

  const selS = { ...inp, cursor: 'pointer' };

  return (
    <>
    {/* Post-creation follow-up prompt */}
    <Modal open={fuModal} onClose={skipFollowUp} title="Schedule First Follow-up?">
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: '#475569' }}>
          Lead <strong>{createdLead?.lead_id}</strong> created. Schedule your first follow-up now so nothing falls through the cracks.
        </p>
      </div>
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Type</label>
            <select value={fuForm.type} onChange={e => setFuForm(f => ({ ...f, type: e.target.value }))} style={selS}>
              {FOLLOWUP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Date & Time *</label>
            <input type="datetime-local" value={fuForm.scheduled_at} onChange={e => setFuForm(f => ({ ...f, scheduled_at: e.target.value }))} min="2000-01-01T00:00" max="2099-12-31T23:59" style={inp} />
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes / Agenda</label>
          <textarea value={fuForm.notes} onChange={e => setFuForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, minHeight: 60, resize: 'vertical' }} placeholder="What to discuss on this follow-up…" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Button variant="ghost" onClick={skipFollowUp}>Skip for now</Button>
          <Button variant="primary" onClick={saveFollowUp} disabled={fuSaving}>{fuSaving ? 'Saving…' : 'Schedule & Open Lead →'}</Button>
        </div>
      </div>
    </Modal>

    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <PageHeader
        title="New Lead"
        subtitle="Fill in the details for the new prospect"
        actions={<Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>← Back</Button>}
      />

      <form onSubmit={handleSubmit}>
        {/* Core Info */}
        <Card style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Basic Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FIELD label="Customer Type *" error={errors.customer_type}>
              <select value={form.customer_type} onChange={e => { setF('customer_type', e.target.value); setTypeData({}); }} style={sel}>
                <option value="">Select type…</option>
                {CUSTOMER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FIELD>

            <FIELD label="Stage">
              <select value={form.stage} onChange={e => setF('stage', e.target.value)} style={sel}>
                {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FIELD>

            <FIELD label="Priority">
              <select value={form.priority} onChange={e => setF('priority', e.target.value)} style={sel}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </FIELD>

            <FIELD label="Source">
              <select value={form.source} onChange={e => setF('source', e.target.value)} style={sel}>
                <option value="">Select source…</option>
                {LEAD_SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </FIELD>

            <FIELD label="State / Region">
              <select value={form.region} onChange={e => setF('region', e.target.value)} style={sel}>
                <option value="">Select state…</option>
                {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FIELD>

            {canAssignLeads && (
              <FIELD label="Assign To">
                <select value={form.assigned_to} onChange={e => setF('assigned_to', e.target.value)} style={sel}>
                  <option value="">Unassigned</option>
                  {teamMembers.map(m => <option key={m._id} value={m._id}>{m.name} ({m.sub_role})</option>)}
                </select>
              </FIELD>
            )}
          </div>
        </Card>

        {/* Contact Info */}
        <Card style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Contact Information</h3>
          {duplicateWarning && (
            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#92400e' }}>
              ⚠️ Possible duplicate detected — Lead ID(s): <strong>{duplicateWarning}</strong>. You may still proceed.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FIELD label="Contact Name">
              <input value={form.contact_name} onChange={e => setF('contact_name', e.target.value)} style={inp} placeholder="Full name" />
            </FIELD>
            <FIELD label="Phone *" error={errors.contact_phone}>
              <input value={form.contact_phone}
                onChange={e => setF('contact_phone', e.target.value)}
                onBlur={() => checkDuplicate(form.contact_phone, form.contact_email)}
                style={{ ...inp, borderColor: errors.contact_phone ? '#ef4444' : '#e2e8f0' }}
                placeholder="+91 98765 43210" />
            </FIELD>
            <FIELD label="Email" error={errors.contact_email}>
              <input type="email" value={form.contact_email}
                onChange={e => setF('contact_email', e.target.value)}
                onBlur={() => checkDuplicate(form.contact_phone, form.contact_email)}
                style={{ ...inp, borderColor: errors.contact_email ? '#ef4444' : '#e2e8f0' }}
                placeholder="contact@example.com" />
            </FIELD>
          </div>
        </Card>

        {/* Type-Specific Fields */}
        {form.customer_type && (
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>
              {form.customer_type} Details
            </h3>
            <TypeSpecificFields type={form.customer_type} data={typeData} setData={setT} />
          </Card>
        )}

        {/* Notes */}
        <Card style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 12 }}>Notes</h3>
          <textarea value={form.notes} onChange={e => setF('notes', e.target.value)}
            placeholder="Initial notes, context, requirements…"
            style={{ ...inp, minHeight: 90, resize: 'vertical' }} />
        </Card>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="ghost" type="button" onClick={() => navigate('/leads')}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} /> Creating…</> : 'Create Lead'}
          </Button>
        </div>
      </form>
    </div>
    </>
  );
}

// TypeSpecificFields is imported from the shared component above
